/**
 * Analytics Pro Dashboard — comprehensive, fully-controlled analytics for Admin/Super Admin.
 *
 * Reads a single `/api/panel/admin/analytics/dashboard?range=...` endpoint and renders:
 *   • Time-range pill bar (clickable): Today · 7d · 30d · 90d · 180d · 365d · Custom
 *   • 8 KPI tiles (clickable, each opens a focused side-drawer with the underlying records)
 *   • Revenue trend (Area + sparkline view) toggleable to Candle (weekly OHLC) view
 *   • Lead-source donut + Booking-status donut + Conversion funnel
 *   • Pest-wise revenue horizontal bar (top 12)
 *   • Technician productivity bar + table (sort by completions / revenue)
 *   • Warranty renewal pipeline (3 buckets) — click to drilldown
 *   • Top customers leaderboard
 *   • Recent activity feed (invoices · bookings · leads, tabbed)
 *   • Export buttons (CSV) + Refresh
 *
 * All visuals are dependency-free SVG (no recharts/chart.js bloat) for fast load.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  IndianRupee, TrendingUp, TrendingDown, AlertTriangle, Users, BookCheck, Inbox,
  ShieldCheck, RefreshCw, Loader2, Calendar, ChevronRight, Download, X,
  PieChart as PieIcon, BarChart3, Activity, Phone, Mail, Filter, Award,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—');
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

const SOURCE_COLORS = {
  website: '#2563eb', whatsapp: '#10b981', phone_call: '#6366f1', manual: '#64748b',
  walk_in: '#f59e0b', referral: '#a855f7', social_media: '#ec4899', chatbot: '#14b8a6',
  other: '#9ca3af',
};

const STATUS_COLORS = {
  scheduled: '#3b82f6', on_the_way: '#f59e0b', in_progress: '#a855f7',
  completed: '#10b981', warranty_active: '#14b8a6', cancelled: '#ef4444',
};

const PIE_PALETTE = ['#10b981', '#3b82f6', '#a855f7', '#f59e0b', '#ec4899', '#14b8a6',
  '#6366f1', '#ef4444', '#84cc16', '#64748b', '#0ea5e9', '#f97316'];

// ============================================================
// RANGE PILL BAR
// ============================================================
const RANGE_PRESETS = [
  { key: 'today', label: 'Today' },
  { key: '7d',    label: '7 days' },
  { key: '30d',   label: '30 days' },
  { key: '90d',   label: '90 days' },
  { key: '180d',  label: '6 months' },
  { key: '365d',  label: '1 year' },
];

function RangeBar({ value, onChange }) {
  const [custom, setCustom] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="analytics-range-bar">
      {RANGE_PRESETS.map((r) => (
        <button
          key={r.key}
          onClick={() => { setCustom(false); onChange({ key: r.key }); }}
          className={`h-9 px-3 rounded-full text-xs font-semibold border transition ${
            !custom && value?.key === r.key
              ? 'bg-emerald-600 border-emerald-600 text-white shadow'
              : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'
          }`}
          data-testid={`range-${r.key}`}
        >
          {r.label}
        </button>
      ))}
      <button
        onClick={() => setCustom((c) => !c)}
        className={`h-9 px-3 rounded-full text-xs font-semibold border transition inline-flex items-center gap-1 ${
          custom ? 'bg-emerald-600 border-emerald-600 text-white shadow' : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'
        }`}
        data-testid="range-custom-toggle"
      >
        <Calendar size={12} /> Custom
      </button>
      {custom && (
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 h-9">
          <Input
            type="date"
            value={value?.from || ''}
            onChange={(e) => onChange({ from: e.target.value, to: value?.to, key: 'custom' })}
            className="h-7 border-0 text-xs p-0 w-28"
            data-testid="range-custom-from"
          />
          <span className="text-slate-400 text-xs">→</span>
          <Input
            type="date"
            value={value?.to || ''}
            onChange={(e) => onChange({ from: value?.from, to: e.target.value, key: 'custom' })}
            className="h-7 border-0 text-xs p-0 w-28"
            data-testid="range-custom-to"
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// KPI TILES
// ============================================================
function KpiTile({ label, value, sublabel, color, icon: Icon, trend, onClick, testid }) {
  const trendPositive = (trend ?? 0) >= 0;
  return (
    <button
      onClick={onClick}
      className={`relative text-left rounded-2xl border bg-gradient-to-br p-4 transition hover:shadow-md hover:-translate-y-0.5 ${color}`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700/80">{label}</div>
        {Icon && <Icon size={16} className="text-slate-700/70" />}
      </div>
      <div className="text-2xl sm:text-[26px] font-extrabold text-slate-900 mt-1.5 leading-tight">{value}</div>
      <div className="flex items-center justify-between mt-1">
        <div className="text-[11px] text-slate-600/90">{sublabel || ''}</div>
        {trend !== undefined && trend !== null && (
          <div className={`text-[11px] font-semibold inline-flex items-center gap-0.5 ${trendPositive ? 'text-emerald-700' : 'text-rose-700'}`}>
            {trendPositive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <ChevronRight size={14} className="absolute right-2 bottom-2 text-slate-400" />
    </button>
  );
}

// ============================================================
// AREA / LINE / CANDLE CHART
// ============================================================
function TrendChart({ trend, candles, mode, onToggleMode }) {
  const data = mode === 'candle' ? candles : trend;
  const isLine = mode !== 'candle';
  const w = 880, h = 240, padL = 50, padR = 16, padT = 16, padB = 30;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const values = isLine ? data.map((d) => d.revenue) : data.flatMap((c) => [c.high, c.low]);
  const maxV = Math.max(1, ...values);
  const minV = 0;

  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const yFor = (v) => padT + innerH - ((v - minV) / (maxV - minV || 1)) * innerH;

  // Line/area path
  let areaPath = '', linePath = '';
  if (isLine && data.length) {
    data.forEach((d, i) => {
      const x = padL + i * xStep;
      const y = yFor(d.revenue);
      linePath += `${i === 0 ? 'M' : 'L'}${x},${y} `;
    });
    areaPath = `${linePath} L ${padL + (data.length - 1) * xStep},${padT + innerH} L ${padL},${padT + innerH} Z`;
  }

  // Y axis ticks
  const yTicks = 4;
  const ticks = [];
  for (let i = 0; i <= yTicks; i++) {
    const v = maxV * (i / yTicks);
    ticks.push({ v, y: yFor(v) });
  }

  // Candle widths
  const candleW = mode === 'candle' && data.length ? Math.max(8, innerW / data.length - 6) : 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="trend-chart">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="font-bold text-slate-900 text-base">Revenue Trend</h3>
          <p className="text-xs text-slate-500">{isLine ? 'Daily revenue' : 'Weekly OHLC candles'}</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => onToggleMode('area')}
            className={`px-2.5 py-1 text-xs font-semibold rounded ${mode === 'area' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            data-testid="trend-mode-area"
          >
            <Activity size={12} className="inline mr-1" /> Area
          </button>
          <button
            onClick={() => onToggleMode('candle')}
            className={`px-2.5 py-1 text-xs font-semibold rounded ${mode === 'candle' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
            data-testid="trend-mode-candle"
          >
            <BarChart3 size={12} className="inline mr-1" /> Candle
          </button>
        </div>
      </div>
      {data.length === 0 ? (
        <div className="text-center py-12 text-sm text-slate-400">No data in range</div>
      ) : (
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="none">
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* grid + y labels */}
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={padL} y1={t.y} x2={w - padR} y2={t.y} stroke="#e2e8f0" strokeDasharray="2 4" />
              <text x={padL - 6} y={t.y + 3} textAnchor="end" fontSize="10" fill="#94a3b8">{fmtMoney(t.v)}</text>
            </g>
          ))}
          {/* x labels (every Nth) */}
          {data.map((d, i) => {
            const skip = Math.ceil(data.length / 8);
            if (i % skip !== 0 && i !== data.length - 1) return null;
            const x = padL + i * xStep;
            const label = isLine ? d.date.slice(5) : d.start.slice(5);
            return <text key={i} x={x} y={h - 10} textAnchor="middle" fontSize="9" fill="#94a3b8">{label}</text>;
          })}

          {isLine && data.length > 0 && (
            <>
              <path d={areaPath} fill="url(#trendFill)" />
              <path d={linePath} fill="none" stroke="#059669" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
              {data.map((d, i) => (
                <circle key={i} cx={padL + i * xStep} cy={yFor(d.revenue)} r={data.length > 60 ? 1.5 : 3}
                        fill="#fff" stroke="#059669" strokeWidth="1.5">
                  <title>{`${d.date}: ${fmtMoney(d.revenue)} · ${d.count} invoices`}</title>
                </circle>
              ))}
            </>
          )}

          {!isLine && data.map((c, i) => {
            const x = padL + i * (innerW / data.length) + (innerW / data.length - candleW) / 2;
            const yH = yFor(c.high), yL = yFor(c.low);
            const yO = yFor(c.open), yC = yFor(c.close);
            const up = c.close >= c.open;
            const top = Math.min(yO, yC), bot = Math.max(yO, yC);
            const color = up ? '#10b981' : '#ef4444';
            return (
              <g key={i}>
                <line x1={x + candleW / 2} y1={yH} x2={x + candleW / 2} y2={yL} stroke={color} strokeWidth="1" />
                <rect x={x} y={top} width={candleW} height={Math.max(2, bot - top)} fill={color} rx="1">
                  <title>{`${c.start} → ${c.end}\nOpen ${fmtMoney(c.open)} · High ${fmtMoney(c.high)} · Low ${fmtMoney(c.low)} · Close ${fmtMoney(c.close)}\nWeekly total ${fmtMoney(c.total)}`}</title>
                </rect>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}

// ============================================================
// DONUT CHART
// ============================================================
function DonutChart({ title, items, valueKey = 'count', labelKey, colorMap, palette = PIE_PALETTE, onSliceClick, testid }) {
  const total = items.reduce((s, i) => s + (i[valueKey] || 0), 0);
  const w = 220, cx = 110, cy = 110, r = 86, ir = 56;
  let acc = 0;
  const slices = items.map((it, i) => {
    const v = it[valueKey] || 0;
    if (!total || !v) return null;
    const pct = v / total;
    const a0 = acc * 2 * Math.PI - Math.PI / 2;
    acc += pct;
    const a1 = acc * 2 * Math.PI - Math.PI / 2;
    const large = pct > 0.5 ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const ix0 = cx + ir * Math.cos(a1), iy0 = cy + ir * Math.sin(a1);
    const ix1 = cx + ir * Math.cos(a0), iy1 = cy + ir * Math.sin(a0);
    const d = `M${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} L${ix0},${iy0} A${ir},${ir} 0 ${large} 0 ${ix1},${iy1} Z`;
    const key = labelKey ? it[labelKey] : '';
    const color = (colorMap && colorMap[key]) || palette[i % palette.length];
    return { d, color, item: it, pct, key };
  }).filter(Boolean);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-slate-900 text-base flex items-center gap-1.5"><PieIcon size={14} className="text-emerald-700" /> {title}</h3>
        <span className="text-[11px] text-slate-500">{total} total</span>
      </div>
      {total === 0 ? (
        <div className="text-center py-12 text-sm text-slate-400">No data in range</div>
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-4">
          <svg viewBox={`0 0 ${w} ${w}`} className="w-44 h-44 shrink-0">
            {slices.map((s, i) => (
              <path
                key={i}
                d={s.d}
                fill={s.color}
                className="cursor-pointer hover:opacity-80 transition"
                onClick={() => onSliceClick?.(s.item)}
              >
                <title>{`${s.key}: ${s.item[valueKey]} (${Math.round(s.pct * 100)}%)`}</title>
              </path>
            ))}
            <circle cx={cx} cy={cy} r={ir - 2} fill="#fff" />
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontWeight="700" fill="#0f172a">{total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fill="#64748b">total</text>
          </svg>
          <div className="flex-1 w-full space-y-1.5 max-h-44 overflow-y-auto">
            {slices.map((s, i) => (
              <button
                key={i}
                onClick={() => onSliceClick?.(s.item)}
                className="w-full flex items-center gap-2 text-left px-2 py-1 rounded hover:bg-slate-50 transition"
                data-testid={`${testid}-row-${i}`}
              >
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="text-xs font-medium text-slate-800 flex-1 truncate capitalize">{String(s.key).replace(/_/g, ' ')}</span>
                <span className="text-xs font-bold text-slate-900">{s.item[valueKey]}</span>
                <span className="text-[10px] text-slate-500 w-10 text-right">{Math.round(s.pct * 100)}%</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// HORIZONTAL BAR
// ============================================================
function HBar({ title, items, labelKey, valueKey, fmt = fmtMoney, subKey, testid, onRowClick }) {
  const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
      <h3 className="font-bold text-slate-900 text-base mb-3 flex items-center gap-1.5">
        <BarChart3 size={14} className="text-emerald-700" /> {title}
      </h3>
      {items.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-400">No data</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => {
            const pct = ((it[valueKey] || 0) / max) * 100;
            return (
              <button
                key={i}
                onClick={() => onRowClick?.(it)}
                className="w-full text-left group"
                data-testid={`${testid}-row-${i}`}
              >
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="font-medium text-slate-800 truncate">{it[labelKey] || '—'}</span>
                  <span className="font-bold text-slate-900">{fmt(it[valueKey])}{subKey && it[subKey] !== undefined ? <span className="text-slate-400 ml-1">· {it[subKey]}</span> : null}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 group-hover:from-emerald-600 group-hover:to-teal-600 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// FUNNEL
// ============================================================
function Funnel({ stages, testid }) {
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
      <h3 className="font-bold text-slate-900 text-base mb-3 flex items-center gap-1.5">
        <Filter size={14} className="text-emerald-700" /> Lead Conversion Funnel
      </h3>
      <div className="space-y-1.5">
        {stages.map((s, i) => {
          const pct = (s.count / max) * 100;
          return (
            <div key={s.stage} className="flex items-center gap-3" data-testid={`${testid}-stage-${s.stage}`}>
              <span className="text-xs w-20 font-medium text-slate-700 capitalize">{s.label}</span>
              <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden relative">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-500"
                  style={{ width: `${pct}%` }}
                />
                <span className="absolute inset-0 flex items-center justify-end pr-2 text-xs font-bold text-slate-900">{s.count}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// DRAWER
// ============================================================
function Drawer({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] bg-black/50" onClick={onClose} data-testid="analytics-drawer">
      <div onClick={(e) => e.stopPropagation()} className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ============================================================
// CSV EXPORT HELPER
// ============================================================
function downloadCsv(filename, rows, columns) {
  if (!rows?.length) return;
  const head = columns.map((c) => c.label).join(',');
  const body = rows.map((r) => columns.map((c) => {
    const v = typeof c.get === 'function' ? c.get(r) : r[c.key];
    const s = (v === null || v === undefined) ? '' : String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  }).join(',')).join('\n');
  const blob = new Blob([head + '\n' + body], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
}

// ============================================================
// MAIN
// ============================================================
export default function AnalyticsDashboard({ api }) {
  const [range, setRange] = useState({ key: '30d' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [trendMode, setTrendMode] = useState('area');
  const [drawer, setDrawer] = useState(null); // { title, content }

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = new URLSearchParams();
      if (range.key === 'custom' && range.from && range.to) {
        qs.set('from_', range.from); qs.set('to', range.to); qs.set('range', 'custom');
      } else {
        qs.set('range', range.key || '30d');
      }
      const res = await api('GET', `/admin/analytics/dashboard?${qs.toString()}`);
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Failed to load analytics');
    } finally { setLoading(false); }
  }, [api, range]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};

  // KPI drilldown loaders
  const openDrilldown = async (kind) => {
    if (kind === 'revenue') {
      const r = await api('GET', '/admin/invoices?page_size=100');
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setDrawer({
        title: `Recent Invoices — ${data?.window?.label || ''}`,
        content: (
          <RecordsTable
            rows={items}
            cols={[
              { label: 'Invoice #', key: 'invoice_number' },
              { label: 'Customer', key: 'customer_name' },
              { label: 'Date', key: 'invoice_date', render: fmtDate },
              { label: 'Total', key: 'total_amount', render: fmtMoney, align: 'right' },
              { label: 'Status', key: 'payment_status' },
            ]}
            onExport={() => downloadCsv('invoices.csv', items, [
              { label: 'invoice', key: 'invoice_number' },
              { label: 'customer', key: 'customer_name' },
              { label: 'date', key: 'invoice_date' },
              { label: 'total', key: 'total_amount' },
              { label: 'status', key: 'payment_status' },
            ])}
          />
        ),
      });
    } else if (kind === 'bookings') {
      const r = await api('GET', '/admin/bookings');
      setDrawer({
        title: `Bookings — ${data?.window?.label || ''}`,
        content: (
          <RecordsTable
            rows={(r.data || []).slice(0, 100)}
            cols={[
              { label: 'Code', key: 'booking_code' },
              { label: 'Customer', key: 'customer_name' },
              { label: 'Date', key: 'scheduled_date' },
              { label: 'Slot', render: (b) => `${b.time_slot_start || '—'}` },
              { label: 'Status', key: 'status' },
              { label: 'Source', key: 'lead_source' },
            ]}
            onExport={() => downloadCsv('bookings.csv', r.data || [], [
              { label: 'code', key: 'booking_code' },
              { label: 'customer', key: 'customer_name' },
              { label: 'phone', key: 'customer_phone' },
              { label: 'date', key: 'scheduled_date' },
              { label: 'slot_start', key: 'time_slot_start' },
              { label: 'slot_end', key: 'time_slot_end' },
              { label: 'status', key: 'status' },
              { label: 'source', key: 'lead_source' },
              { label: 'amount', key: 'final_amount' },
            ])}
          />
        ),
      });
    } else if (kind === 'leads') {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        headers: { 'X-Admin-Token': 'entorid-admin-2026' },
      }).then((x) => x.json());
      setDrawer({
        title: `Leads — ${data?.window?.label || ''}`,
        content: (
          <RecordsTable
            rows={(Array.isArray(r) ? r : []).slice(0, 100)}
            cols={[
              { label: 'Name', key: 'name' },
              { label: 'Phone', key: 'phone' },
              { label: 'Pest', key: 'pest_label' },
              { label: 'Source', render: (l) => l.lead_source || l.source },
              { label: 'Status', key: 'status' },
              { label: 'Created', key: 'created_at', render: fmtDate },
            ]}
            onExport={() => downloadCsv('leads.csv', Array.isArray(r) ? r : [], [
              { label: 'name', key: 'name' },
              { label: 'phone', key: 'phone' },
              { label: 'pest', key: 'pest_label' },
              { label: 'source', get: (l) => l.lead_source || l.source },
              { label: 'status', key: 'status' },
              { label: 'created', key: 'created_at' },
            ])}
          />
        ),
      });
    } else if (kind === 'warranties') {
      const r = await api('GET', '/admin/analytics/warranty-pipeline?days=90');
      setDrawer({
        title: 'Warranty Pipeline · next 90 days',
        content: (
          <RecordsTable
            rows={(r.data?.items || [])}
            cols={[
              { label: 'Warranty', key: 'warranty_number' },
              { label: 'Customer', key: 'customer_name' },
              { label: 'Pest', key: 'service_name' },
              { label: 'Expires', key: 'end_date', render: fmtDate },
              { label: 'Days', key: 'days_remaining' },
            ]}
            onExport={() => downloadCsv('warranty-pipeline.csv', r.data?.items || [], [
              { label: 'warranty', key: 'warranty_number' },
              { label: 'customer', key: 'customer_name' },
              { label: 'phone', key: 'customer_phone' },
              { label: 'pest', key: 'service_name' },
              { label: 'end_date', key: 'end_date' },
              { label: 'days_remaining', key: 'days_remaining' },
            ])}
          />
        ),
      });
    } else if (kind === 'outstanding') {
      const r = await api('GET', '/admin/invoices?status=pending&page_size=100');
      const raw = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      const items = raw.filter((x) => (x.payment_status || '') !== 'paid');
      setDrawer({
        title: 'Outstanding Invoices',
        content: (
          <RecordsTable
            rows={items}
            cols={[
              { label: 'Invoice', key: 'invoice_number' },
              { label: 'Customer', key: 'customer_name' },
              { label: 'Due', key: 'amount_due', render: fmtMoney, align: 'right' },
              { label: 'Date', key: 'invoice_date', render: fmtDate },
              { label: 'Status', key: 'payment_status' },
            ]}
            onExport={() => downloadCsv('outstanding.csv', items, [
              { label: 'invoice', key: 'invoice_number' },
              { label: 'customer', key: 'customer_name' },
              { label: 'amount_due', key: 'amount_due' },
              { label: 'date', key: 'invoice_date' },
            ])}
          />
        ),
      });
    } else if (kind === 'customers') {
      const r = await api('GET', '/admin/customers');
      setDrawer({
        title: 'Customers',
        content: (
          <RecordsTable
            rows={(r.data || []).slice(0, 100)}
            cols={[
              { label: 'Name', key: 'name' },
              { label: 'Phone', key: 'phone' },
              { label: 'City', key: 'city' },
              { label: 'Last booked', key: 'last_booked_at', render: fmtDate },
            ]}
            onExport={() => downloadCsv('customers.csv', r.data || [], [
              { label: 'name', key: 'name' },
              { label: 'phone', key: 'phone' },
              { label: 'email', key: 'email' },
              { label: 'city', key: 'city' },
              { label: 'last_booked', key: 'last_booked_at' },
            ])}
          />
        ),
      });
    }
  };

  if (loading && !data) return (
    <div className="text-center text-slate-400 py-12">
      <Loader2 className="inline animate-spin" size={24} />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="text-emerald-700" /> Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {data?.window?.label || '—'} · click any tile, chart segment, or row to drill down
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="analytics-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <RangeBar value={range} onChange={(v) => setRange((r) => ({ ...r, ...v }))} />

      {err && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700 flex items-center gap-2">
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
        <KpiTile
          label="Revenue"
          value={fmtMoney(kpis.revenue)}
          sublabel={`${kpis.invoices || 0} invoices · ${fmtMoney(kpis.revenue_paid)} paid`}
          icon={IndianRupee}
          trend={kpis.revenue_growth_pct}
          color="from-emerald-50 to-emerald-100 border-emerald-200"
          onClick={() => openDrilldown('revenue')}
          testid="kpi-revenue"
        />
        <KpiTile
          label="Outstanding"
          value={fmtMoney(kpis.outstanding)}
          sublabel={`${kpis.outstanding_count || 0} unpaid invoices`}
          icon={AlertTriangle}
          color="from-rose-50 to-rose-100 border-rose-200"
          onClick={() => openDrilldown('outstanding')}
          testid="kpi-outstanding"
        />
        <KpiTile
          label="Bookings"
          value={kpis.bookings || 0}
          sublabel={`${kpis.bookings_completed || 0} completed`}
          icon={BookCheck}
          color="from-blue-50 to-blue-100 border-blue-200"
          onClick={() => openDrilldown('bookings')}
          testid="kpi-bookings"
        />
        <KpiTile
          label="Leads"
          value={kpis.leads || 0}
          sublabel={`${kpis.leads_converted || 0} converted · ${kpis.conversion_pct || 0}%`}
          icon={Inbox}
          color="from-amber-50 to-amber-100 border-amber-200"
          onClick={() => openDrilldown('leads')}
          testid="kpi-leads"
        />
        <KpiTile
          label="Active Warranties"
          value={kpis.active_warranties || 0}
          sublabel={`${kpis.warranties_expiring_30d || 0} expiring 30d`}
          icon={ShieldCheck}
          color="from-teal-50 to-teal-100 border-teal-200"
          onClick={() => openDrilldown('warranties')}
          testid="kpi-warranties"
        />
        <KpiTile
          label="Active Customers"
          value={kpis.active_customers || 0}
          sublabel={`${kpis.total_customers || 0} total in DB`}
          icon={Users}
          color="from-indigo-50 to-indigo-100 border-indigo-200"
          onClick={() => openDrilldown('customers')}
          testid="kpi-customers"
        />
        <KpiTile
          label="Avg Order Value"
          value={fmtMoney(kpis.avg_order_value)}
          sublabel={`based on ${kpis.invoices || 0} invoices`}
          icon={Award}
          color="from-violet-50 to-violet-100 border-violet-200"
          onClick={() => openDrilldown('revenue')}
          testid="kpi-aov"
        />
        <KpiTile
          label="Conversion"
          value={`${kpis.conversion_pct || 0}%`}
          sublabel={`${kpis.leads_converted || 0} of ${kpis.leads || 0} leads`}
          icon={TrendingUp}
          color="from-pink-50 to-pink-100 border-pink-200"
          onClick={() => openDrilldown('leads')}
          testid="kpi-conversion"
        />
      </div>

      {/* Revenue trend */}
      <TrendChart
        trend={data?.trend || []}
        candles={data?.candles || []}
        mode={trendMode}
        onToggleMode={setTrendMode}
      />

      {/* Donuts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DonutChart
          title="Lead Sources"
          items={data?.lead_sources || []}
          labelKey="source"
          colorMap={SOURCE_COLORS}
          onSliceClick={(it) => setDrawer({
            title: `Lead Source · ${it.source}`,
            content: (
              <div className="space-y-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-4">
                  <div><span className="text-slate-500">Source:</span> <span className="capitalize font-bold">{String(it.source).replace(/_/g, ' ')}</span></div>
                  <div><span className="text-slate-500">Leads:</span> <span className="font-bold">{it.count}</span></div>
                  <div><span className="text-slate-500">Converted:</span> <span className="font-bold">{it.converted}</span></div>
                  <div><span className="text-slate-500">Conversion %:</span> <span className="font-bold">{it.conversion_pct}%</span></div>
                </div>
                <p className="text-xs text-slate-500">
                  Tip: high-volume sources with low conversion are good candidates to invest more in agent training or scripted flows.
                </p>
              </div>
            ),
          })}
          testid="donut-leadsource"
        />
        <DonutChart
          title="Booking Status Mix"
          items={data?.booking_status || []}
          labelKey="status"
          colorMap={STATUS_COLORS}
          onSliceClick={(it) => openDrilldown('bookings')}
          testid="donut-status"
        />
      </div>

      {/* Funnel + Warranty buckets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Funnel stages={data?.funnel || []} testid="conversion-funnel" />
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="warranty-buckets">
          <h3 className="font-bold text-slate-900 text-base mb-3 flex items-center gap-1.5">
            <ShieldCheck size={14} className="text-emerald-700" /> Warranty Pipeline
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {(data?.warranty_pipeline || []).map((b, i) => (
              <button
                key={i}
                onClick={() => openDrilldown('warranties')}
                className="rounded-xl border bg-gradient-to-br from-slate-50 to-white p-3 text-center hover:shadow-md transition"
                data-testid={`warranty-bucket-${i}`}
              >
                <div className="text-3xl font-extrabold text-slate-900">{b.count}</div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{b.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pest revenue + Top customers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <HBar
          title="Pest-wise Revenue"
          items={data?.pest_revenue || []}
          labelKey="service"
          valueKey="revenue"
          subKey="count"
          testid="pest-revenue"
        />
        <HBar
          title="Top Customers"
          items={(data?.top_customers || []).map((c) => ({ ...c, _label: `${c.name} · ${c.phone}` }))}
          labelKey="_label"
          valueKey="spent"
          subKey="invoices"
          testid="top-customers"
        />
      </div>

      {/* Technician productivity */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="tech-productivity">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-1.5">
            <Users size={14} className="text-emerald-700" /> Technician Productivity
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCsv('technicians.csv', data?.technicians || [], [
              { label: 'name', key: 'name' },
              { label: 'employee_code', key: 'employee_code' },
              { label: 'completed', key: 'completed' },
              { label: 'revenue', key: 'revenue' },
              { label: 'last_completion', key: 'last_completion' },
            ])}
            data-testid="tech-export"
          >
            <Download size={12} className="mr-1" /> CSV
          </Button>
        </div>
        {(data?.technicians || []).length === 0 ? (
          <div className="text-center py-8 text-sm text-slate-400">No completions yet in this period.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 pr-2">Technician</th>
                  <th className="text-left py-2">Code</th>
                  <th className="text-right py-2 pr-2">Completed</th>
                  <th className="text-right py-2 pr-2">Revenue</th>
                  <th className="text-left py-2">Last completion</th>
                </tr>
              </thead>
              <tbody>
                {data.technicians.map((t) => (
                  <tr key={t.technician_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 pr-2 font-medium text-slate-900">{t.name}</td>
                    <td className="py-2 text-xs text-slate-500">{t.employee_code || '—'}</td>
                    <td className="py-2 pr-2 text-right font-bold">{t.completed}</td>
                    <td className="py-2 pr-2 text-right font-bold text-emerald-700">{fmtMoney(t.revenue)}</td>
                    <td className="py-2 text-xs text-slate-500">{fmtDateTime(t.last_completion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent activity */}
      <RecentActivity recent={data?.recent || {}} api={api} />

      <Drawer open={!!drawer} title={drawer?.title} onClose={() => setDrawer(null)}>
        {drawer?.content}
      </Drawer>
    </div>
  );
}

// ============================================================
// Recent Activity (tabbed)
// ============================================================
function RecentActivity({ recent }) {
  const [tab, setTab] = useState('invoices');
  const tabs = [
    { key: 'invoices', label: 'Invoices', icon: IndianRupee },
    { key: 'bookings', label: 'Bookings', icon: BookCheck },
    { key: 'leads',    label: 'Leads',    icon: Inbox },
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="recent-activity">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-bold text-slate-900 text-base flex items-center gap-1.5">
          <Activity size={14} className="text-emerald-700" /> Recent Activity
        </h3>
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1 text-xs font-semibold rounded inline-flex items-center gap-1 ${tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}
              data-testid={`recent-tab-${t.key}`}
            >
              <t.icon size={11} /> {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'invoices' && (
        <RecordsTable
          rows={recent?.invoices || []}
          cols={[
            { label: 'Invoice', key: 'invoice_number' },
            { label: 'Customer', key: 'customer_name' },
            { label: 'Date', key: 'invoice_date', render: fmtDate },
            { label: 'Total', key: 'total_amount', render: fmtMoney, align: 'right' },
            { label: 'Status', key: 'payment_status' },
          ]}
        />
      )}
      {tab === 'bookings' && (
        <RecordsTable
          rows={recent?.bookings || []}
          cols={[
            { label: 'Code', key: 'booking_code' },
            { label: 'Customer', key: 'customer_name' },
            { label: 'Date', key: 'scheduled_date' },
            { label: 'Status', key: 'status' },
            { label: 'Source', key: 'lead_source' },
            { label: 'Amount', key: 'final_amount', render: fmtMoney, align: 'right' },
          ]}
        />
      )}
      {tab === 'leads' && (
        <RecordsTable
          rows={recent?.leads || []}
          cols={[
            { label: 'Name', key: 'name' },
            { label: 'Phone', key: 'phone' },
            { label: 'Pest', key: 'pest_label' },
            { label: 'Source', render: (l) => l.lead_source || l.source },
            { label: 'Status', key: 'status' },
            { label: 'Created', key: 'created_at', render: fmtDate },
          ]}
        />
      )}
    </div>
  );
}

// ============================================================
// Records Table (used in drawers + recent feed)
// ============================================================
function RecordsTable({ rows, cols, onExport }) {
  if (!rows?.length) return <div className="text-center py-8 text-sm text-slate-400">No records.</div>;
  return (
    <div>
      {onExport && (
        <div className="flex justify-end mb-2">
          <Button variant="outline" size="sm" onClick={onExport} data-testid="records-export">
            <Download size={12} className="mr-1" /> Export CSV
          </Button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              {cols.map((c, i) => (
                <th key={i} className={`py-2 pr-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                {cols.map((c, j) => {
                  const v = typeof c.render === 'function'
                    ? c.render(c.key ? r[c.key] : r, r)
                    : r[c.key];
                  return <td key={j} className={`py-2 pr-2 text-slate-800 ${c.align === 'right' ? 'text-right font-semibold' : ''}`}>{v ?? '—'}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
